import * as React from 'react';

import { faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Formatter as CategoryFormatter } from '@admin/components/categories/formatter';
import { Hero } from '@admin/components/items/hero';
import { MaterialCategoryPreview as ItemMaterialCategoryPreview } from '@admin/components/items/material_category_preview';
import { Metadata as ItemMetadata } from '@admin/components/items/metadata';
import { PartCategoryPreview as ItemPartCategoryPreview } from '@admin/components/items/part_category_preview';
import { client } from '@admin/libraries/apollo';
import { useMaterialCategoriesQuery, usePartCategoriesQuery } from '@admin/schema';
import { IItem } from '@admin/types/item';
import { IItemGroup } from '@admin/types/item_group';

interface ItemPackagingFieldsProps {
  item: IItem;
  onItemChange?: (item: IItem) => void;
}

const MaterialCategorySelect: React.FC<{
  item: IItem;
  selection: number | undefined;
  filters?: { kind?: string };
  onChange: (categoryId: number | undefined) => void;
}> = ({ item, selection, filters, onChange }) => {
  const { data, loading } = useMaterialCategoriesQuery({ client });

  if (loading) return <div>Loading...</div>;

  const categories = data?.categories || [];
  const filteredCategories = filters?.kind ? categories.filter((cat) => cat.kind === filters.kind) : categories;

  return (
    <select
      name={`items[${item.id}][material_category]`}
      className="form-control form-control-inline"
      value={selection || ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
    >
      <option value=""> - Material - </option>
      {filteredCategories.map((category) => (
        <option key={category.id} value={category.id}>
          {category.name}
        </option>
      ))}
    </select>
  );
};

const PartCategorySelect: React.FC<{
  item: IItem;
  selection: number | undefined;
  onChange: (categoryId: number | undefined) => void;
}> = ({ item, selection, onChange }) => {
  const { data, loading } = usePartCategoriesQuery({ client });

  if (loading) return <div>Loading...</div>;

  const categories = data?.categories || [];

  // Filter categories that have a parent
  const categoriesWithParent = categories.filter((cat) => cat.parentID);

  // Create a lookup map for parent names using the same categories array
  const parentLookup = categories.reduce(
    (lookup, category) => {
      lookup[category.id] = category.name;
      return lookup;
    },
    {} as Record<string, string>,
  );

  // Group categories by parent name
  const groupedCategories = categoriesWithParent.reduce(
    (groups, category) => {
      const parentName = category.parentID ? parentLookup[category.parentID] || 'Other' : 'Other';
      if (!groups[parentName]) groups[parentName] = [];
      groups[parentName].push(category);
      return groups;
    },
    {} as Record<string, typeof categoriesWithParent>,
  );

  return (
    <select
      name={`items[${item.id}][part_category]`}
      className="form-control form-control-inline"
      value={selection || ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
    >
      <option value=""> - Part - </option>
      {Object.entries(groupedCategories).map(([parentName, cats]) => (
        <optgroup key={parentName} label={parentName}>
          {cats.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
};

const ItemPackagingFields: React.FC<ItemPackagingFieldsProps> = ({ item, onItemChange }) => {
  const handlePackagingTypeChange = (type: 'boxed' | 'crated' | 'wrapped') => {
    const updatedItem = {
      ...item,
      packaging: {
        ...item.packaging,
        type,
        // Reset category IDs when packaging type changes
        material_category_id: undefined,
        part_category_id: undefined,
        materialCategoryId: undefined,
        partCategoryId: undefined,
      },
    };
    onItemChange?.(updatedItem);
  };

  const handleMaterialCategoryChange = (categoryId: number | undefined) => {
    const updatedItem: IItem = {
      ...item,
      packaging: {
        ...item.packaging,
        type: item.packaging!.type, // Must be present - user selected radio button first
        material_category_id: categoryId,
        materialCategoryId: categoryId,
      },
    };
    onItemChange?.(updatedItem);
  };

  const handlePartCategoryChange = (categoryId: number | undefined) => {
    const updatedItem: IItem = {
      ...item,
      packaging: {
        ...item.packaging,
        type: item.packaging!.type, // Must be present - user selected radio button first
        part_category_id: categoryId,
        partCategoryId: categoryId,
      },
    };
    onItemChange?.(updatedItem);
  };

  return (
    <div>
      <div>
        <div className="radio-inline">
          <label>
            <input
              name={`items[${item.id}][packaging]`}
              type="radio"
              value="boxed"
              checked={item.packaging?.type === 'boxed'}
              onChange={() => handlePackagingTypeChange('boxed')}
            />
            Boxed
          </label>
        </div>
        <div className="radio-inline">
          <label>
            <input
              name={`items[${item.id}][packaging]`}
              type="radio"
              value="crated"
              checked={item.packaging?.type === 'crated'}
              onChange={() => handlePackagingTypeChange('crated')}
            />
            Crated
          </label>
        </div>
        <div className="radio-inline">
          <label>
            <input
              name={`items[${item.id}][packaging]`}
              type="radio"
              value="wrapped"
              checked={item.packaging?.type === 'wrapped'}
              onChange={() => handlePackagingTypeChange('wrapped')}
            />
            Wrapped
          </label>
        </div>
      </div>

      {item.packaging && (
        <div>
          {item.packaging.type === 'boxed' && (
            <MaterialCategorySelect
              item={item}
              selection={item.packaging.material_category_id || item.packaging.materialCategoryId}
              filters={{ kind: 'box' }}
              onChange={handleMaterialCategoryChange}
            />
          )}
          {item.packaging.type !== 'boxed' && (
            <PartCategorySelect
              item={item}
              selection={item.packaging.part_category_id || item.packaging.partCategoryId}
              onChange={handlePartCategoryChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

export interface ItemsTableProps {
  items: IItem[];
  groups?: IItemGroup[];
  mode?: string;
  onItemChange?: (item: IItem) => void;
}

export const ItemsTable: React.FC<ItemsTableProps> = ({ items, groups = [], mode, onItemChange }) => {
  const findGroup = (item: IItem): IItemGroup | undefined => {
    if (groups && item.group_id) {
      return groups.find((group) => group.id === item.group_id);
    }
    return undefined;
  };

  return (
    <table className="table">
      <tbody>
        {items.map((item) => {
          const group = findGroup(item);
          return (
            <tr key={item.id}>
              <td className="col-nowrap col-zero text-left">
                <Hero selection={item} width={40} height={40} />
              </td>
              <td className="col-fill col-nowrap text-left">
                {mode === 'package' && <ItemPackagingFields item={item} onItemChange={onItemChange} />}
                {group && (
                  <span>
                    <FontAwesomeIcon icon={faLink} /> <span>to</span>{' '}
                    <span>
                      <CategoryFormatter resource={group} />
                    </span>{' '}
                    <span>#{group.id}</span>{' '}
                    {item.packaging && (
                      <span>
                        <span>as</span> <ItemMaterialCategoryPreview item={item} />{' '}
                        <ItemPartCategoryPreview item={item} />
                      </span>
                    )}
                  </span>
                )}
              </td>
              <td className="col-nowrap col-zero text-right">
                <ItemMetadata item={item} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
